/**
 * ReactionBar - emoji reaction buttons for raffle viewers
 * Compact FAB with expandable menu on mobile
 */

import { useState, useCallback, useRef, useEffect } from "react";
import type { ReactionEmoji } from "@/lib/websocket";
import { haptic } from "@/lib/telegram";
import styles from "./ReactionBar.module.css";

interface ReactionBarProps {
  /** Callback when reaction is sent */
  onReaction: (emoji: ReactionEmoji) => void;
  /** Whether reactions are enabled */
  disabled?: boolean;
}

const REACTION_EMOJIS: readonly ReactionEmoji[] = ["🔥", "❤️", "🎉"];
const COOLDOWN_MS = 100; // Minimum time between reactions (faster for responsiveness)

export function ReactionBar({ onReaction, disabled = false }: ReactionBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pressedEmoji, setPressedEmoji] = useState<ReactionEmoji | null>(null);
  const lastReactionTimeRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside (with delay to prevent accidental close during rapid tapping)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      // Small delay to allow rapid taps on menu buttons without accidentally closing
      // Check if touch/click is truly outside the container
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        // Use requestAnimationFrame to avoid closing during touch sequence
        requestAnimationFrame(() => {
          setIsOpen(false);
        });
      }
    };

    // Use mousedown for desktop, but use touchend (not touchstart) for mobile
    // touchstart fires before the tap completes, causing issues with rapid tapping
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchend", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchend", handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    haptic("light");
    setIsOpen((prev) => !prev);
  }, []);

  const handleReaction = useCallback(
    (emoji: ReactionEmoji) => {
      if (disabled) return;

      // Simple cooldown check
      const now = Date.now();
      if (now - lastReactionTimeRef.current < COOLDOWN_MS) {
        return;
      }
      lastReactionTimeRef.current = now;

      // Visual feedback
      setPressedEmoji(emoji);
      haptic("light");

      // Send reaction
      onReaction(emoji);

      // Reset pressed state quickly - keep menu open for multiple reactions
      setTimeout(() => {
        setPressedEmoji(null);
      }, 100);
    },
    [onReaction, disabled]
  );

  return (
    <div ref={containerRef} className={styles.container}>
      {/* Expandable emoji buttons */}
      <div className={`${styles.menu} ${isOpen ? styles.menuOpen : ""}`}>
        {REACTION_EMOJIS.slice()
          .reverse()
          .map((emoji, index) => (
            <button
              key={emoji}
              className={`${styles.menuButton} ${pressedEmoji === emoji ? styles.pressed : ""}`}
              style={{ transitionDelay: isOpen ? `${index * 50}ms` : "0ms" }}
              onClick={() => handleReaction(emoji)}
              disabled={disabled || !isOpen}
              aria-label={`React with ${emoji}`}
            >
              <span className={styles.emoji}>{emoji}</span>
            </button>
          ))}
      </div>

      {/* Main FAB button */}
      <button
        className={`${styles.fab} ${isOpen ? styles.fabOpen : ""}`}
        onClick={handleToggle}
        disabled={disabled}
        aria-label={isOpen ? "Close reactions" : "Open reactions"}
        aria-expanded={isOpen}
      >
        <span className={styles.fabIcon}>{isOpen ? "✕" : "🔥"}</span>
        <span className={styles.fabRipple} />
      </button>
    </div>
  );
}

export default ReactionBar;
